// @flow

import React, { type Node, useCallback, useState, useLayoutEffect } from 'react';
import { type Country } from '@tectonics/prs/country';
import { usePaymentSpec } from '@tectonics/prs/payment-spec';
import { type PaymentMethodCode } from '@tectonics/prs/payment-method';
import { PaymentMethod, PaymentMethodBox, SelectablePaymentMethodBox } from './payment-method';
import { useFormStatusContext } from '@tectonics/prs/form-status/use-form-status-context';
import { getSpecImplementedPaymentMethods } from '@tectonics/prs/support';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useAnalyticsContext } from '@tectonics/analytics';
import shopifyLaf from '@tectonics/look-and-feel/shopify';

import { useValidation, hasUserInputValidationError, hasSubmitError } from '@tectonics/prs/validation';

import { scrollToTop, scrollToFirstFieldWithError } from '@tectonics/screen';

import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { isElementVisible } from '@tectonics/look-and-feel/commons/dom-utils';

import { CustomerForm } from './customer';
import { FormButtons } from './form-buttons';
import { CaptchaForm } from './captcha';
import { BrazilianTermsForCrossBorder } from './brazilian-terms-for-crossborder';

type Props = {|
  onSubmit: (boolean, boolean) => Promise<boolean>,
|};

export function Form({ onSubmit }: Props): Node {
  const spec = usePaymentSpec();
  const analytics = useAnalyticsContext();
  const formHasBeenSent = useFormHasBeenSent();
  const [selectedPaymentMethodCode] = useUserInputValue('selectedPaymentMethod');

  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [, setFormStatus] = useFormStatusContext();
  const handleSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();
      (async () => {
        const hasErrors = Array.from(document.querySelectorAll('[data-error]')).filter(isElementVisible).length > 0;

        setFormStatus('sent');
        setHasScrolled(false);
        await onSubmit(formHasBeenSent, hasErrors);

      })();
    }, [onSubmit, setFormStatus, formHasBeenSent, analytics]);

  const validation = useValidation();

  useLayoutEffect(() => {
    if (!hasScrolled && hasUserInputValidationError(validation)) {
      scrollToFirstFieldWithError();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation]);

  useLayoutEffect(() => {
    if (!hasScrolled && hasSubmitError(validation)) {
      scrollToTop();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation]);

  const implementedPaymentMethods = getSpecImplementedPaymentMethods(spec, shopifyLaf.prsSupport);

  const dataTest = implementedPaymentMethods.length === 1 ? {} : { 'data-test-name': 'payment-method' };

  return (
    <form onSubmit={handleSubmit} {...dataTest}>
      <div>
        {implementedPaymentMethods.length === 1 ? (
          <PaymentMethodBox>
            <CustomerForm />
            <PaymentMethod />
          </PaymentMethodBox>
        ) : (
          implementedPaymentMethods.map(paymentMethodCode => (
            <SelectablePaymentMethodBox
              key={paymentMethodCode}
              paymentMethodCode={paymentMethodCode}
              title={<PaymentMethodName paymentMethodCode={paymentMethodCode} country={spec.country} />}
              opened={paymentMethodCode === selectedPaymentMethodCode}
            >
              {paymentMethodCode === selectedPaymentMethodCode && (
                <>
                  <CustomerForm />
                  <PaymentMethod />
                </>
              )}
            </SelectablePaymentMethodBox>
          ))
        )}
      </div>

      <CaptchaForm />
      <BrazilianTermsForCrossBorder />
      <FormButtons />
    </form>
  );
}

type PaymentMethodNameProps = {|
  country: Country,
  paymentMethodCode: PaymentMethodCode,
|};

function PaymentMethodName({ paymentMethodCode, country }: PaymentMethodNameProps): Node {
  switch (country) {
    case 'br':
      return <BrazilianPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'ar':
      return <ArgentinePaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'bo':
      return <BolivianPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'cl':
      return <ChileanPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'co':
      return <ColombianPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'ec':
      return <EcuadorianPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'pe':
      return <PeruvianPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'mx':
      return <MexicanPaymentMethodName paymentMethod={paymentMethodCode} />;
    case 'uy':
      return <UruguayanPaymentMethodName paymentMethod={paymentMethodCode} />;
    default:
      throw new Error(`Payment method "${paymentMethodCode}" has no implemented name for country: "${country}"`);
  }
}

type CountryPaymentMethodNameProps = {|
  paymentMethod: PaymentMethodCode,
|};

function BolivianPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'cash':
      return 'Efectivo';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function ChileanPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'multicaja':
      return 'Klap (Multicaja)';

    case 'creditcard':
      return 'Tarjeta de crédito';

    case 'debitcard':
      return 'Tarjeta de débito';

    case 'sencillito':
      return 'Sencillito';

    case 'servipag':
      return 'ServiPag';

    case 'webpay':
      return 'Webpay';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function EcuadorianPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'safetypay-cash':
      return 'Pago en Efectivo';

    case 'safetypay-online':
      return 'Transferencia Bancaria';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function PeruvianPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de crédito';

    case 'debitcard':
      return 'Tarjeta de débito';

    case 'safetypay':
      return 'Safetypay';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function BrazilianPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Cartão de crédito';

    case 'boleto':
      return 'Boleto bancário';

    case 'banktransfer':
      return 'Transferência bancária';

    case 'pix':
      return 'Pix';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function MexicanPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';

    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'paynet':
      return 'Pago con efectivo via Paynet';

    case 'spei':
      return 'SPEI';

    case 'oxxo':
      return 'OXXO';

    case 'cash':
    case 'oxxopay':
      return 'OXXOPay';

    case 'ewallet':
      return 'MercadoPago';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function ColombianPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'eft':
      return 'Pago con PSE';
    case 'creditcard':
      return 'Tarjeta de crédito';
    case 'ewallet':
      return 'NEQUI';
    case 'cash':
      return 'Efecty';
    case 'externalpayprovider':
      return 'PSE Avanza';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function ArgentinePaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';
    case 'debitcard':
      return 'Tarjeta de Débito';

    case 'cash':
      return 'Cupones de pago';

    case 'ewallet':
      return 'MercadoPago';

    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}

function UruguayanPaymentMethodName({ paymentMethod }: CountryPaymentMethodNameProps): Node {
  switch (paymentMethod) {
    case 'creditcard':
      return 'Tarjeta de Crédito';
    case 'debitcard':
      return 'Tarjeta de Débito';
    default:
      throw new Error(`Payment method "${paymentMethod}" has no implemented name`);
  }
}
